import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon
} from '@mui/icons-material';
import TopicsVisualization from './TopicsVisualization';
import SemanticAnalysisVisualization from './SemanticAnalysisVisualization';

interface AIResponseStreamingProps {
  isStreaming: boolean;
  streamedContent: string;
  isComplete: boolean;
  showRawByDefault?: boolean;
}

const AIResponseStreaming: React.FC<AIResponseStreamingProps> = ({
  isStreaming,
  streamedContent,
  isComplete,
  showRawByDefault = false,
}) => {
  const [showRawResponse, setShowRawResponse] = useState(showRawByDefault);
  const streamingContentRef = useRef<HTMLDivElement>(null);

  // Parse token usage and conversation ID from metadata at the end of streamed content
  const parseTokenUsage = (content: string): { input: number; output: number; total: number; conversationId?: string } | null => {
    try {
      // Look for __METADATA__ followed by JSON
      const metadataMatch = content.match(/__METADATA__\s*(\{[^}]*\})/);
      if (metadataMatch) {
        const metadata = JSON.parse(metadataMatch[1]);
        if (metadata.input_tokens !== undefined && metadata.output_tokens !== undefined) {
          return {
            input: metadata.input_tokens,
            output: metadata.output_tokens,
            total: metadata.total_tokens || (metadata.input_tokens + metadata.output_tokens),
            conversationId: metadata.conversation_id,
          };
        }
      }
    } catch (error) {
      console.error('Error parsing token usage:', error);
    }
    return null;
  };

  // Parse model information from __MODEL__ metadata
  const parseModelInfo = (content: string): string | null => {
    try {
      const modelMatch = content.match(/__MODEL__\s*(\{[^}]*\})/);
      if (modelMatch) {
        const modelData = JSON.parse(modelMatch[1]);
        return modelData.model || null;
      }
    } catch (error) {
      console.error('Error parsing model info:', error);
    }
    return null;
  };

  const tokenUsage = isComplete ? parseTokenUsage(streamedContent) : null;
  const modelInfo = isComplete ? parseModelInfo(streamedContent) : null;

  // Parse JSON data from streamed content
  const parseTopicsData = (content: string): any | null => {
    try {
      // Remove metadata sections (__METADATA__ and __MODEL__)
      let cleanContent = content
        .replace(/__METADATA__\s*\{[^}]*\}/g, '')
        .replace(/__MODEL__\s*\{[^}]*\}/g, '')
        .trim();
      
      // Try to extract JSON from markdown code block
      const codeBlockMatch = cleanContent.match(/```json\s*([\s\S]*?)\s*```/);
      if (codeBlockMatch) {
        return JSON.parse(codeBlockMatch[1]);
      }
      
      // Try to parse the entire content as JSON
      const jsonMatch = cleanContent.match(/\{[\s\S]*"topics"[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error parsing topics data:', error);
    }
    return null;
  };

  // Parse semantic analysis data (intent categories)
  const parseSemanticData = (content: string): any | null => {
    try {
      // Remove metadata sections (__METADATA__ and __MODEL__)
      let cleanContent = content
        .replace(/__METADATA__\s*\{[^}]*\}/g, '')
        .replace(/__MODEL__\s*\{[^}]*\}/g, '')
        .trim();
      
      // Try to extract JSON from markdown code block
      const codeBlockMatch = cleanContent.match(/```json\s*([\s\S]*?)\s*```/);
      if (codeBlockMatch) {
        const parsed = JSON.parse(codeBlockMatch[1]);
        if (parsed.intent_categories) {
          return parsed;
        }
      }
      
      // Try to parse the entire content as JSON
      const jsonMatch = cleanContent.match(/\{[\s\S]*"intent_categories"[\s\S]*\}/);
      if (jsonMatch) {
        return JSON.parse(jsonMatch[0]);
      }
    } catch (error) {
      console.error('Error parsing semantic data:', error);
    }
    return null;
  };

  const topicsData = isComplete ? parseTopicsData(streamedContent) : null;
  const semanticData = isComplete ? parseSemanticData(streamedContent) : null;

  // Auto-scroll to bottom when streaming content updates
  useEffect(() => {
    if (streamingContentRef.current && isStreaming) {
      streamingContentRef.current.scrollTop = streamingContentRef.current.scrollHeight;
    }
  }, [streamedContent, isStreaming]);

  // During streaming - show live content
  if (isStreaming) {
    return (
      <Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
          <CircularProgress size={24} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            AI Analysis in Progress
          </Typography>
        </Box>

        {/* Linear progress for initial loading */}
        {!streamedContent && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Initializing AI analysis and connecting to Guardrail...
            </Typography>
            <LinearProgress />
          </Box>
        )}

        {/* Streaming content area */}
        <Box
          ref={streamingContentRef}
          sx={{
            p: 2,
            backgroundColor: '#000000',
            borderRadius: 2,
            maxHeight: '500px',
            overflow: 'auto',
            minHeight: streamedContent ? 'auto' : '100px'
          }}
        >
          {streamedContent ? (
            <Typography
              variant="body2"
              component="pre"
              sx={{
                whiteSpace: 'pre-wrap',
                wordWrap: 'break-word',
                fontFamily: 'inherit',
                m: 0,
                color: '#ffffff'
              }}
            >
              {streamedContent}
            </Typography>
          ) : (
            <Typography
              variant="body2"
              sx={{
                fontStyle: 'italic',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '100px',
                color: '#999999'
              }}
            >
              Waiting for AI response...
            </Typography>
          )}
        </Box>
      </Box>
    );
  }

  // After completion - show collapsible raw response
  if (isComplete && streamedContent) {
    return (
      <Box sx={{ my: 2 }}>
        {/* Topics Visualization - Show if JSON data is parsed */}
        {topicsData && topicsData.topics && (
          <Box sx={{ mb: 4 }}>
            <TopicsVisualization data={topicsData} />
          </Box>
        )}
        
        {/* Semantic Analysis Visualization - Show if intent categories data is parsed */}
        {semanticData && semanticData.intent_categories && (
          <Box sx={{ mb: 4 }}>
            <SemanticAnalysisVisualization data={semanticData} />
          </Box>
        )}
        
        {/* Raw AI Response Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 0,
            px: 0,
          }}
        >
          {/* Clickable icon and label */}
          <Box
            onClick={() => setShowRawResponse(!showRawResponse)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              cursor: 'pointer',
              color: 'text.primary',
              '&:hover': {
                opacity: 0.7,
              },
            }}
          >
            {showRawResponse ? (
              <ExpandLessIcon sx={{ fontSize: 20 }} />
            ) : (
              <ExpandMoreIcon sx={{ fontSize: 20 }} />
            )}
            <Typography sx={{ fontWeight: 600 }}>
              Raw AI Response
              {(tokenUsage || modelInfo) && (
                <Typography 
                  component="span" 
                  sx={{ 
                    fontWeight: 400, 
                    fontSize: '0.875rem',
                    color: 'text.secondary',
                    ml: 1
                  }}
                >
                  (
                  {tokenUsage && (
                    <>
                      token usage: {tokenUsage.input.toLocaleString()} + {tokenUsage.output.toLocaleString()} = {tokenUsage.total.toLocaleString()}
                      {tokenUsage.conversationId && `, Conversation: ${tokenUsage.conversationId}`}
                    </>
                  )}
                  {tokenUsage && modelInfo && ', '}
                  {modelInfo && `Model: ${modelInfo}`}
                  )
                </Typography>
              )}
            </Typography>
          </Box>
          
          {/* Spacer */}
          <Box sx={{ flexGrow: 1 }} />
        </Box>

        {/* Raw Response Content - Conditional */}
        {showRawResponse && (
          <Box
            sx={{
              backgroundColor: '#000000',
              maxHeight: '600px',
              overflow: 'auto',
              p: 2,
              borderRadius: '4px',
            }}
          >
            {streamedContent ? (
              <Typography
                variant="body2"
                component="pre"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  fontFamily: 'inherit',
                  m: 0,
                  color: '#ffffff'
                }}
              >
                {streamedContent}
              </Typography>
            ) : (
              <Typography variant="body2" sx={{ color: '#999999' }}>
                Analysis results will be displayed here...
              </Typography>
            )}
          </Box>
        )}
      </Box>
    );
  }

  // Default - nothing to show
  return null;
};

export default AIResponseStreaming;

